import { useEffect, type FC } from 'react';

import { useSession } from '@eneco/auth/useSession';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import {
  NextBestActionBlockDto,
  NextBestActionCardResponse,
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByDeliveryAddressIdNextBestActions,
} from '@eneco/dc-be/client';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { Box, Heading, PageGrid, Stack } from '@eneco/sparky';
import { styled } from '@eneco/sparky/stitches';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { DashboardOverviewRendering } from '@sitecore/types/DashboardOverview';

import { DashboardBlock } from './components/DashboardBlock';
import { AddressFilter } from '../../../components/AddressFilter';
import DataErrorNotification from '../../../components/DataErrorNotification';
import { RoundedSkeleton } from '../../../components/Loading/RoundedSkeleton';
import { useAddressOptions } from '../../../hooks/useAddressOptions';

const createBlockOrderMapping = (fields: DashboardOverviewRendering['fields']) => {
  const blockToOrder: Record<NextBestActionBlockDto, number> = {
    Pay: fields.blocksOrderNumbers.payBlockOrderNumber.value,
    Adv: fields.blocksOrderNumbers.advBlockOrderNumber.value,
    Usage: fields.blocksOrderNumbers.usageBlockOrderNumber.value,
    Sit: fields.blocksOrderNumbers.sitBlockOrderNumber.value,
    Eng: fields.blocksOrderNumbers.engBlockOrderNumber.value,
    Pers: fields.blocksOrderNumbers.persBlockOrderNumber.value,
    Any: 999,
  };

  return blockToOrder;
};

const GRID_AREAS = ['one', 'two', 'three', 'four', 'five', 'six'];

const sortCardsByOrder = (
  cards: NextBestActionCardResponse[] | null | undefined,
  blockOrderMapping: Record<NextBestActionBlockDto, number>,
): NextBestActionCardResponse[] => {
  if (!cards) return [];

  return [...cards]
    .sort((a, b) => {
      const orderA = blockOrderMapping[a.block || 'Any'];
      const orderB = blockOrderMapping[b.block || 'Any'];
      return orderA - orderB;
    })
    .filter(card => card.key !== 'Fallback');
};

const Dashboard: FC = () => {
  const { fields } = useContent<DashboardOverviewRendering>();

  const {
    selectedAccount: { crmAccountNumber: accountNumber },
    isProspect,
  } = useSelfServiceAccount();
  const { data: session } = useSession();
  const { addressIdentifier, isLoading: isLoadingAddress, error: errorAddress } = useAddressOptions();
  const { trackEventBE } = useTracking();

  const {
    data: unwrappedData,
    isLoading: isLoadingNextBestActions,
    error: errorNextBestActions,
  } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByDeliveryAddressIdNextBestActions,
    {
      path: {
        accountNumber,
        deliveryAddressId: addressIdentifier,
      },
    },
    [`/accounts/${accountNumber}/delivery-addresses/${addressIdentifier}/next-best-actions`],
    session,
  );

  const data = unwrapData(unwrappedData);

  const blockOrderMapping = createBlockOrderMapping(fields);
  const sortedCards = sortCardsByOrder(data?.cards, blockOrderMapping);
  const isLoading = isLoadingAddress || isLoadingNextBestActions;
  const fetchingAddressError = errorAddress != null && addressIdentifier === null;
  const fetchingNextBestActionsError = errorNextBestActions != null && !data;

  useEffect(() => {
    trackEventBE('nba_page_view', {
      status: 'pageView',
      data: {
        // nba_page: 'active contract', to see if we still want to do this, because we don't have this data available in the API at the moment and we don't want to add a call here just for tracking
        nba_items: sortedCards.map(card => ({ nba_item: card.key })),
      },
    });
  }, [sortedCards, trackEventBE]);

  if (!isLoading && (fetchingAddressError || fetchingNextBestActionsError)) {
    return (
      <PageGrid>
        <PageGrid.Item gridColumn="1/-1">
          <Box
            paddingLeft={{ initial: '2', sm: '0' }}
            paddingTop={{ initial: '10', md: '16' }}
            paddingBottom={{ initial: '8', md: '12' }}>
            <DataErrorNotification />
          </Box>
        </PageGrid.Item>
      </PageGrid>
    );
  }

  if (isProspect) return null;

  return (
    <PageGrid>
      <PageGrid.Item gridColumn="1/-1">
        <Box
          paddingLeft={{ initial: '2', sm: '0' }}
          paddingTop={{ initial: '10', md: '16' }}
          paddingBottom={{ initial: '8', md: '12' }}>
          <Stack alignX="justify" direction="row" gap={4} alignY="center" wrap>
            <Heading as="h1" size={{ initial: 'M', lg: 'L' }}>
              {fields.content.title.value}
            </Heading>
            <Stack.Item grow />
            <Stack.Item>
              <AddressFilter
                label={fields.content.addressFilterLabel?.value || ''}
                onFocus={() => {
                  trackEventBE('dashboard_dropdown_selected', {
                    status: 'click',
                  });
                }}
              />
            </Stack.Item>
          </Stack>
        </Box>

        <Box paddingBottom={6}>
          <StyledDashboard>
            {isLoading
              ? GRID_AREAS.map(gridArea => {
                  return (
                    <StyledDashboardItem key={gridArea}>
                      <RoundedSkeleton height={250} />
                    </StyledDashboardItem>
                  );
                })
              : GRID_AREAS.map((gridArea, index) => {
                  const card = sortedCards[index];

                  if (!card) {
                    return null;
                  }

                  return (
                    <StyledDashboardItem key={gridArea} data-block={card.block} data-card={card.key}>
                      <DashboardBlock card={card} addressIdentifier={addressIdentifier} />
                    </StyledDashboardItem>
                  );
                })}
          </StyledDashboard>
        </Box>
      </PageGrid.Item>
    </PageGrid>
  );
};

const StyledDashboard = styled('div', {
  columns: '1',
  '@md': {
    columns: '2',
  },
  '@lg': {
    columns: '3 auto',
  },
});

const StyledDashboardItem = styled('div', {
  display: 'inline-block',
  width: '100%',
  marginBottom: '$6',
  '&:empty': {
    display: 'none',
  },
});

export default Dashboard;
